import { useState } from 'react';
import emailjs from 'emailjs-com';
import { events } from '../analytics';
import { useReservationsContext } from '../context/ReservationsContext';

// Configuracion de EmailJS
const EMAILJS_CONFIG = {
    serviceID: process.env.REACT_APP_EMAILJS_SERVICE_ID,
    templateID: process.env.REACT_APP_EMAILJS_RESERVATION_TEMPLATE_ID || process.env.REACT_APP_EMAILJS_TEMPLATE_ID,
    userID: process.env.REACT_APP_EMAILJS_USER_ID
};

// Limites de longitud
const MAX_NAME_LENGTH = 100;
const MAX_EMAIL_LENGTH = 254;
const MAX_PHONE_LENGTH = 20;
const MAX_POSTAL_CODE_LENGTH = 10;

// Opciones de países para envío
const SHIPPING_COUNTRIES = [
  { value: 'ES-PENINSULA', label: 'España - Península, Ceuta y Melilla' },
  { value: 'ES-CANARIAS', label: 'España - Islas Canarias' },
  { value: 'PT', label: 'Portugal' },
  { value: 'FR', label: 'Francia' },
  { value: 'IT', label: 'Italia' },
];

const GRAN_CANARIA_ONLY = { value: 'ES-GRANCANARIA', label: 'España - Gran Canaria' };

// Obtener label del país por su valor
const getCountryLabel = (countryValue) => {
  if (countryValue === GRAN_CANARIA_ONLY.value) return GRAN_CANARIA_ONLY.label;
  const country = SHIPPING_COUNTRIES.find(c => c.value === countryValue);
  return country ? country.label : countryValue;
};

// Rate limiting
const RATE_LIMIT_KEY = 'reservation_submissions';
const MAX_SUBMISSIONS = 5;
const RATE_LIMIT_WINDOW = 60 * 60 * 1000; // 1 hora

const isConfigValid = () => {
    const { serviceID, templateID, userID } = EMAILJS_CONFIG;
    return Boolean(serviceID && templateID && userID);
};

const checkRateLimit = () => {
    try {
        const submissions = JSON.parse(localStorage.getItem(RATE_LIMIT_KEY) || '[]');
        const now = Date.now();
        const recentSubmissions = submissions.filter(time => now - time < RATE_LIMIT_WINDOW);

        if (recentSubmissions.length >= MAX_SUBMISSIONS) {
            return false;
        }

        recentSubmissions.push(now);
        localStorage.setItem(RATE_LIMIT_KEY, JSON.stringify(recentSubmissions));
        return true;
    } catch {
        return true;
    }
};

const isValidEmail = (email) => {
    if (!email || email.length > MAX_EMAIL_LENGTH) return false;
    const emailRegex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;
    return emailRegex.test(email);
};

const isValidPhone = (phone) => {
    if (!phone || phone.length > MAX_PHONE_LENGTH) return false;
    const phoneRegex = /^[+]?[\d\s()-]{9,20}$/;
    return phoneRegex.test(phone.replace(/\s/g, ''));
};

const sanitizeInput = (input, maxLength) => {
    if (!input) return '';
    return input
        .trim()
        .replace(/[<>]/g, '')
        .substring(0, maxLength);
};

export const useReservationForm = (product) => {
    const { createReservation, isProductReserved, getProductStatus } = useReservationsContext();

    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone: '',
        country: '',
        postalCode: '',
        privacyAccepted: false,
        storyConsent: false
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitStatus, setSubmitStatus] = useState(null);
    const [errors, setErrors] = useState({});

    const validateForm = () => {
        const newErrors = {};
        const name = formData.name.trim();
        const email = formData.email.trim();
        const phone = formData.phone.trim();

        if (!name) {
            newErrors.name = 'El nombre es requerido';
        } else if (name.length > MAX_NAME_LENGTH) {
            newErrors.name = `El nombre no puede exceder ${MAX_NAME_LENGTH} caracteres`;
        }

        if (!email) {
            newErrors.email = 'El email es requerido';
        } else if (!isValidEmail(email)) {
            newErrors.email = 'El email no es valido';
        }

        if (!phone) {
            newErrors.phone = 'El telefono es requerido';
        } else if (!isValidPhone(phone)) {
            newErrors.phone = 'El telefono no es valido (min. 9 digitos)';
        }

        // Validar país de envío
        const effectiveCountry = product?.onlyCanary ? 'ES-GRANCANARIA' : formData.country;
        if (!effectiveCountry) {
            newErrors.country = 'Selecciona un país de envío';
        }

        // Validar restricción de envío (productos delicados solo a Gran Canaria)
        if (product?.onlyCanary && effectiveCountry !== 'ES-GRANCANARIA') {
            newErrors.country = 'Este producto solo puede enviarse a Gran Canaria';
        }

        // Validar código postal
        const postalCode = formData.postalCode.trim();
        if (!postalCode) {
            newErrors.postalCode = 'El código postal es requerido';
        } else if (postalCode.length < 4) {
            newErrors.postalCode = 'Introduce un código postal válido';
        }

        // Validar aceptación de política de privacidad
        if (!formData.privacyAccepted) {
            newErrors.privacy = 'Debes aceptar la política de privacidad';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;

        // Manejar checkboxes
        if (type === 'checkbox') {
            setFormData(prev => ({
                ...prev,
                [name]: checked
            }));
            // Limpiar error cuando el usuario marca el checkbox
            if (errors[name] || (name === 'privacyAccepted' && errors.privacy)) {
                setErrors(prev => ({
                    ...prev,
                    [name]: '',
                    privacy: ''
                }));
            }
            return;
        }

        let sanitizedValue = value;
        if (name === 'name') {
            sanitizedValue = value.substring(0, MAX_NAME_LENGTH);
        } else if (name === 'email') {
            sanitizedValue = value.substring(0, MAX_EMAIL_LENGTH);
        } else if (name === 'phone') {
            sanitizedValue = value.substring(0, MAX_PHONE_LENGTH);
        } else if (name === 'postalCode') {
            sanitizedValue = value.substring(0, MAX_POSTAL_CODE_LENGTH);
        }

        setFormData(prev => ({
            ...prev,
            [name]: sanitizedValue
        }));

        if (errors[name]) {
            setErrors(prev => ({
                ...prev,
                [name]: ''
            }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Verificar si el producto esta vendido
        if (getProductStatus(product?.id) === 'sold') {
            setSubmitStatus('product_sold');
            setTimeout(() => setSubmitStatus(null), 5000);
            return;
        }

        // Verificar si ya esta reservado
        if (isProductReserved(product?.id)) {
            setSubmitStatus('already_reserved');
            setTimeout(() => setSubmitStatus(null), 5000);
            return;
        }

        if (!isConfigValid()) {
            setSubmitStatus('config_error');
            return;
        }

        if (!validateForm()) {
            return;
        }

        if (!checkRateLimit()) {
            setSubmitStatus('rate_limit');
            setTimeout(() => setSubmitStatus(null), 5000);
            return;
        }

        setIsSubmitting(true);
        setSubmitStatus(null);

        try {
            // Preparar datos del cliente
            const customerInfo = {
                name: sanitizeInput(formData.name, MAX_NAME_LENGTH),
                email: sanitizeInput(formData.email, MAX_EMAIL_LENGTH),
                phone: sanitizeInput(formData.phone, MAX_PHONE_LENGTH)
            };

            // Primero crear reserva en Firebase
            await createReservation(product.id, customerInfo);

            // Luego enviar email
            const { serviceID, templateID, userID } = EMAILJS_CONFIG;

            const templateParams = {
                from_name: customerInfo.name,
                from_email: customerInfo.email,
                from_phone: customerInfo.phone,
                product_name: product?.name || 'Producto',
                product_id: product?.id || 'N/A',
                product_price: product?.price ? `${product.price.toFixed(2)} EUR` : 'N/A',
                to_name: 'Duo a Color',
                type: ' Reserva ',
                reply_to: customerInfo.email,
                message: `Solicitud de reserva para: ${product?.name || 'Producto'}
ID: ${product?.id || 'N/A'}
Precio: ${product?.price ? product.price.toFixed(2) + ' EUR' : 'N/A'}
ESTADO: RESERVADO EN SISTEMA (expira en 48h)

--- DATOS DE ENVÍO ---
País: ${getCountryLabel(product?.onlyCanary ? 'ES-GRANCANARIA' : formData.country)}
Código Postal: ${formData.postalCode}
${product?.onlyCanary ? '⚠️ PRODUCTO DELICADO - Solo envío a Gran Canaria' : ''}

--- DATOS DEL CLIENTE ---
Nombre: ${formData.name}
Email: ${formData.email}
Teléfono: ${formData.phone}

--- CONSENTIMIENTOS RGPD ---
Politica de Privacidad: ACEPTADA
Compartir Historia: ${formData.storyConsent ? 'SI, AUTORIZADO' : 'NO AUTORIZADO'}`,
                date: new Date().toLocaleString('es-ES', {
                    weekday: 'long',
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                }),
                // Registro de consentimientos RGPD para aval legal
                privacy_accepted: 'SI',
                story_consent: formData.storyConsent ? 'SI' : 'NO'
            };

            await emailjs.send(serviceID, templateID, templateParams, userID);

            setSubmitStatus('success');
            setFormData({ name: '', email: '', phone: '', country: '', postalCode: '', privacyAccepted: false, storyConsent: false });
            setErrors({});

            // Trackear reserva completada
            events.reservationComplete(product?.name, product?.id);

        } catch (error) {
            if (process.env.NODE_ENV !== 'production') {
                console.error('Error sending reservation:', error);
            }

            // Manejar error especifico de producto ya reservado
            if (error.message === 'Este producto ya esta reservado') {
                setSubmitStatus('already_reserved');
            } else {
                setSubmitStatus('error');
            }
            setTimeout(() => setSubmitStatus(null), 5000);
        } finally {
            setIsSubmitting(false);
        }
    };

    const resetForm = () => {
        setFormData({ name: '', email: '', phone: '', country: '', postalCode: '', privacyAccepted: false, storyConsent: false });
        setErrors({});
        setSubmitStatus(null);
    };

    return {
        formData,
        handleChange,
        handleSubmit,
        isSubmitting,
        submitStatus,
        errors,
        resetForm,
        limits: {
            maxName: MAX_NAME_LENGTH,
            maxEmail: MAX_EMAIL_LENGTH,
            maxPhone: MAX_PHONE_LENGTH,
            maxPostalCode: MAX_POSTAL_CODE_LENGTH
        },
        shippingOptions: {
            countries: SHIPPING_COUNTRIES,
            granCanariaOnly: GRAN_CANARIA_ONLY
        }
    };
};
